import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import styled from '@emotion/styled';
import { Grid } from '@kc-console/common-ui';
import { SplitPanelId, SplitPanelInnerId } from '@kc-console/context';

import { ScrollObserverRootWrapper } from '../ScrollObserverWrapper';
import {
  LayoutBodyContainerWrapper,
  LayoutContentSectionWrapper,
  LayoutHeaderContainerWrapper,
} from './Wrapper';

import type { DefaultComponentProps, PanelPortalProps, PanelSectionProps } from './types';

export default function PanelLayout(props: DefaultComponentProps) {
  const { className, children } = props;
  return (
    <PanelLayoutWrapper container flexDirection={'column'} className={className}>
      {children}
    </PanelLayoutWrapper>
  );
}

export const PanelSection = (props: PanelSectionProps) => {
  const { headerView, children, enableScrollObserver = true } = props;
  return (
    <PanelContentSection>
      <PanelHeaderContainer>{headerView}</PanelHeaderContainer>
      {enableScrollObserver ? (
        <ScrollObserverRootWrapper
          rootComponent={<PanelBodyContainer>{children}</PanelBodyContainer>}
        />
      ) : (
        <PanelBodyContainer>{children}</PanelBodyContainer>
      )}
    </PanelContentSection>
  );
};

export const PanelPortal = (props: PanelPortalProps) => {
  const { localVisible, targetPanel, transitionendCallback } = props;
  const targetRef = useRef<HTMLDivElement>(null);
  const targetInnerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    targetRef.current = document.getElementById(SplitPanelId) as HTMLDivElement;
    targetInnerRef.current = document.getElementById(SplitPanelInnerId) as HTMLDivElement;

    targetRef.current.addEventListener('transitionend', transitionendCallback);

    return () => {
      if (targetRef.current) {
        targetRef.current.removeEventListener('transitionend', transitionendCallback);
      }
    };
  }, [transitionendCallback]);

  return (
    localVisible &&
    targetRef.current &&
    targetInnerRef.current &&
    targetPanel &&
    createPortal(targetPanel, targetInnerRef.current)
  );
};

const PanelLayoutWrapper = styled(Grid)`
  width: 100%;
  height: 100%;
`;

const PanelContentSection = styled(LayoutContentSectionWrapper)`
  overflow: auto;
`;

const PanelHeaderContainer = styled(LayoutHeaderContainerWrapper)``;
const PanelBodyContainer = styled(LayoutBodyContainerWrapper)`
  flex: 1;
  // NOTE. ScrollObserverEntry Element의 offsetTop을 위해 position relative 적용
  position: relative;
  padding: 0 32px 32px 32px;
  overflow: auto;
`;
